<script setup lang="ts">
import { watchOnce } from '@vueuse/core';

const { $localePath } = useNuxtApp();
const { parameters, utmSource, utmMedium, utmCampaign } = useParameters();
const { closeModal } = useLayoutStore();
const { numberOfDepositMethods, defaultSelectedDepositMethod } = usePayments();
const { isLoading } = storeToRefs(usePaymentsStore());
const { walletHasFirstDeposit } = useWallet();
const { userCanOnlyWithdraw } = useUserLimits();

const { userCurrency, userCountry, userLanguage, userId, userHasDocument } =
  useUserData();

const utmCampaignValue = computed(() => {
  return utmCampaign.value || '';
});

const utmSourceValue = computed(() => {
  return utmSource.value || '';
});

const utmMediumValue = computed(() => {
  return utmMedium.value || '';
});

const hasAnyDepositMethod = computed(() => {
  return numberOfDepositMethods.value >= 1;
});

const minDepositAmount = computed(
  () => parameters.value?.min_deposit_value || 100,
);

const maxDepositAmount = computed(
  () => parameters.value?.max_deposit_value || 4900000,
);

const isPaymentResultShow = computed(() => {
  if (currentScreenType.value.includes('result')) {
    return true;
  }

  return false;
});

const currentScreenType = ref<DepositScreenType>('payment_list');

const changePaymentScreen = (type: DepositScreenType) => {
  currentScreenType.value = type;
};

const activeFirstDepositCountdown = computed<boolean>(() => {
  return (
    !!parameters.value?.first_deposit_bonus &&
    !!parameters.value?.first_deposit_bonus_active &&
    !walletHasFirstDeposit.value
  );
});

const firstDepositMessage = computed(() => {
  if (
    !parameters.value?.deposit_message ||
    parameters.value?.deposit_message === 'null'
  ) {
    return;
  }

  return parameters.value?.deposit_message;
});

const activeFirstDepositMessage = computed<boolean>(() => {
  return !!activeFirstDepositCountdown.value && !!firstDepositMessage.value;
});

const clickToCloseModal = () => {
  closeModal();
};

const goToChangeCountry = () => {
  closeModal();
  navigateTo({
    path: $localePath('/user/account'),
    query: {
      highlight: 'infos',
    },
  });
};

const { currentDepositMethod } = storeToRefs(useWalletStore());
const { selectDepositMethod } = useWalletStore();

watchOnce(
  () => isLoading.value,
  () => {
    selectDepositMethod(defaultSelectedDepositMethod.value);
  },
);
</script>

<template>
  <ClientOnly>
    <div class="w-full flex flex-col gap-5 p-6">
      <template v-if="userCanOnlyWithdraw">
        <div class="alert alert-error">
          <span>{{ $t('error.generic') }}</span>
        </div>
      </template>

      <template v-else-if="hasAnyDepositMethod">
        <div
          v-if="!isPaymentResultShow"
          class="flex flex-row items-center gap-4"
        >
          <NuxtIcon
            class="text-auth-texts/80 cursor-pointer"
            name="arrows/arrow_left"
            @click="clickToCloseModal"
          />
          <div class="flex flex-col">
            <span class="text-auth-texts font-bold text-base text-left">
              {{ $t('deposit.general.modal_title') }}
            </span>
            <span class="text-auth-texts/70 font-normal text-xs text-left">
              {{ $t('deposit.general.modal_subtitle') }}
            </span>
          </div>
        </div>

        <DepositMethodSelector
          v-if="hasAnyDepositMethod && !isLoading"
          :is-result-screen="isPaymentResultShow"
          :selected-deposit-method="currentDepositMethod"
          @select-deposit-method="selectDepositMethod"
        />

        <DepositDefaultLayout
          v-if="currentDepositMethod !== 'crypto'"
          :active-first-deposit-message="activeFirstDepositMessage"
          :first-deposit-message="firstDepositMessage"
          :is-result-screen="isPaymentResultShow"
          :max-deposit-amount="maxDepositAmount"
          :min-deposit-amount="minDepositAmount"
          :selected-deposit-method="currentDepositMethod"
          :user-country="userCountry"
          :user-currency="userCurrency"
          :user-has-document="userHasDocument"
          :user-id="userId"
          :user-language="userLanguage"
          :utm-campaign="utmCampaignValue"
          :utm-medium="utmMediumValue"
          :utm-source="utmSourceValue"
          @change-screen="changePaymentScreen"
        />

        <DepositCrypto
          v-else
          :active-first-deposit-message="activeFirstDepositMessage"
          :first-deposit-message="firstDepositMessage"
          :max-deposit-amount="maxDepositAmount"
          :min-deposit-amount="minDepositAmount"
          :user-country="userCountry"
          :user-currency="userCurrency"
          :user-id="userId"
          :user-language="userLanguage"
          :utm-campaign="utmCampaignValue"
          :utm-medium="utmMediumValue"
          :utm-source="utmSourceValue"
        />
      </template>

      <template v-if="!hasAnyDepositMethod">
        <div class="alert alert-warning">
          {{
            $t('deposit.general.no_pay_method', {
              currency: userCurrency,
              country: userCountry,
            })
          }}
        </div>

        <div
          v-if="!walletHasFirstDeposit"
          class="alert alert-success"
        >
          <button @click="goToChangeCountry">
            {{ $t('deposit.general.can_change_country') }}
          </button>
        </div>
      </template>

      <!-- Compliance oficial no checkout/depósito (ENG-404): advertência +
           18+ + identificação, padronizado com o resto do site -->
      <BaseComplianceInfo />
    </div>
  </ClientOnly>
</template>

<style module lang="postcss">
.accordion {
  @apply w-full flex flex-col gap-3;
}
</style>
